import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { get, getTodos, mensagemDeErro, patch, post } from '../../nucleo/api';
import { campoEmpresaDoRegistro, empresaEscolhida } from '../../nucleo/empresa/empresa-do-registro';
import { hojeIso, nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CONTRATOS, limpar, nomeDoPapel, type Duplicidade, type Funcionario } from './tipos';
import { corpoVinculo } from './Vinculos';

/** A conferência tem o que mostrar: CPF já cadastrado (aqui, noutra empresa ou noutro ambiente) ou nome e nascimento iguais. */
const temAviso = (d: Duplicidade | null): d is Duplicidade => !!d && (!!d.cpf || d.cpf_em_outra_empresa || d.cpf_em_outro_workspace || d.possiveis.length > 0);

/**
 * Fase 2 (W16; pendência da W2b): antes de admitir, confere a duplicidade enquanto a pessoa digita (CPF completo, ou nome e
 * nascimento). CPF que já existe no workspace oferece "Acrescentar vínculo nesta empresa" com os dados de contrato do
 * formulário; o de uma empresa que a pessoa não vê só diz que existe (nada do cadastro de lá aparece).
 */
function AvisoDuplicidade({ valores, aoAcrescentar }: { valores: Record<string, unknown>; aoAcrescentar: (funcionarioId: string) => void }) {
  const { t } = useTranslation('pessoas');
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const [dup, setDup] = useState<Duplicidade | null>(null);
  const nome = String(valores.nome ?? '').trim();
  const cpf = String(valores.cpf ?? '').replace(/\D/g, '');
  const nascimento = typeof valores.nascimento === 'string' ? valores.nascimento : '';
  useEffect(() => {
    if (cpf.length !== 11 && !(nome && nascimento)) {
      setDup(null);
      return;
    }
    let vivo = true;
    const relogio = setTimeout(() => {
      get<Duplicidade>('/pessoas/funcionarios-duplicidade', limpar({ nome: nome || '-', cpf: cpf.length === 11 ? cpf : undefined, nascimento: nascimento || undefined })).then(
        (d) => vivo && setDup(d),
        () => vivo && setDup(null),
      );
    }, 500);
    return () => {
      vivo = false;
      clearTimeout(relogio);
    };
  }, [nome, cpf, nascimento]);
  const acrescentar = useMutation({
    mutationFn: async () => {
      const corpo = corpoVinculo(valores, s.pode('pessoas.confidencial'));
      if (dup?.cpf) {
        await post(`/pessoas/funcionarios/${dup.cpf.id}/vinculos`, corpo);
        return dup.cpf.id;
      }
      return (await post<{ funcionario_id: string }>('/pessoas/vinculos/por-cpf', { ...corpo, cpf })).funcionario_id;
    },
    onSuccess: async (id) => {
      await qc.invalidateQueries({ queryKey: ['/pessoas/funcionarios'] });
      await qc.invalidateQueries({ queryKey: ['pessoas'] });
      toast.sucesso(t('vinculos.acrescentado'));
      aoAcrescentar(id);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  if (!temAviso(dup)) return null;
  const podeAcrescentar = (!!dup.cpf || dup.cpf_em_outra_empresa) && !dup.vinculo_aqui && s.pode('pessoas.criar');
  return (
    <div role="status" data-duplicidade="" className="flex flex-col gap-2 rounded-lg border border-laranja px-3 py-2 text-sm text-laranja">
      {dup.cpf && (
        <p>
          {t(dup.vinculo_aqui ? 'duplicidade.cpfComVinculoAqui' : 'duplicidade.cpfExiste', { nome: nomePessoa(dup.cpf.nome) })}{' '}
          <Link to={`/pessoas/pessoas/funcionario/${dup.cpf.id}`} className="font-medium underline">
            {t('duplicidade.abrirFicha')}
          </Link>
        </p>
      )}
      {!dup.cpf && dup.cpf_em_outra_empresa && <p>{t(dup.vinculo_aqui ? 'duplicidade.outraEmpresaComVinculoAqui' : 'duplicidade.outraEmpresa')}</p>}
      {dup.cpf_em_outro_workspace && <p>{t('duplicidade.outroWorkspace')}</p>}
      {podeAcrescentar && (
        <div className="flex flex-wrap items-center gap-2">
          <Botao tamanho="sm" carregando={acrescentar.isPending} onClick={() => acrescentar.mutate()}>
            {t('vinculos.acrescentar')}
          </Botao>
          <span className="text-xs text-tinta-2">{t('duplicidade.acrescentarAjuda')}</span>
        </div>
      )}
      {dup.possiveis.length > 0 && (
        <p>
          {t('duplicidade.possiveis', { count: dup.possiveis.length })}{' '}
          {dup.possiveis.map((p, i) => (
            <span key={p.id}>
              {i > 0 && ', '}
              <Link to={`/pessoas/pessoas/funcionario/${p.id}`} className="font-medium underline">
                {nomePessoa(p.nome)}
              </Link>
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

/** Admissão (sem `funcionario`) ou edição de um funcionário. */
export function FormFuncionario({ aberto, aoFechar, funcionario, aoSalvar }: { aberto: boolean; aoFechar: () => void; funcionario?: Funcionario; aoSalvar?: (id: string) => void }) {
  const { t } = useTranslation('pessoas');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const edicao = !!funcionario;
  const s = useSessao();
  // Salário é confidencial (review r3): sem a permissão, o campo some e não vai no corpo.
  const verSalario = s.pode('pessoas.confidencial');
  // Lote de 21/10 (item 1): na edição, sem a permissão, os dados pessoais (CPF, nascimento, telefone e campos personalizados)
  // chegam vazios e somem do formulário; na admissão, quem admite informa (escrever não revela).
  const pessoais = verSalario || !edicao;
  // Fase 2 (W16): valores ao vivo da admissão, para a conferência de duplicidade.
  const [valores, setValores] = useState<Record<string, unknown>>({});
  // Val1 (cadastro prévio, 28/10): o papel de acesso do convite é escolhido aqui; a função só sugere (preenche o campo vazio).
  // Review rval1-12 spec (B1): só os papéis que quem admite pode conceder (a mesma régua do envio; antes, todos os da conta).
  const papeis = useQuery({
    queryKey: ['pessoas', 'papeis-admissao'],
    queryFn: () => getTodos<{ id: string; nome: string; codigo: string; de_fabrica: boolean }>('/pessoas/funcionarios/papeis-admissao').then((r) => r.itens.filter((p) => p.codigo !== 'dono')),
    enabled: aberto && !edicao,
  });
  const funcaoEscolhida = !edicao && typeof valores.funcao_id === 'string' ? valores.funcao_id : null;
  const sugerido = useQuery({
    queryKey: ['pessoas', 'papel-da-funcao', funcaoEscolhida],
    queryFn: async () => (await getTodos<{ id: string; papel_id: string | null }>('/pessoas/funcoes')).itens.find((f) => f.id === funcaoEscolhida)?.papel_id ?? null,
    enabled: aberto && !!funcaoEscolhida,
  });
  const [ajuste, setAjuste] = useState<{ n: number; dados: Record<string, unknown> } | undefined>(undefined);
  // A sugestão da função só entra se o papel está entre os concedíveis (como no convite pela ficha).
  const sugestaoValida = sugerido.data && papeis.data?.some((p) => p.id === sugerido.data) ? sugerido.data : null;
  useEffect(() => {
    if (sugestaoValida) setAjuste((a) => ({ n: (a?.n ?? 0) + 1, dados: { papel_id: sugestaoValida } }));
  }, [sugestaoValida]);

  const campos: DefCampo[] = [
    // V2 verticais: a admissão sem empresa na aba (sem filtro, várias permitidas) pede a empresa do vínculo principal.
    ...(edicao ? [] : campoEmpresaDoRegistro(s.empresas, { rotulo: tn('empresa.rotulo'), ajuda: t('campos.empresaAdmissaoAjuda') })),
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true, sensivel: 'nome' },
    // Lote de 19/10 (resposta do Gustavo): CPF e admissão podem faltar e ser completados depois; já informados, não se apagam.
    ...(pessoais
      ? ([
          { nome: 'cpf', rotulo: t('campos.cpf'), tipo: 'documento', documento: 'cpf', obrigatorio: !!funcionario?.cpf, largura: 'meia', ajuda: funcionario?.cpf ? undefined : t('campos.cpfAjuda') },
          { nome: 'nascimento', rotulo: t('campos.nascimento'), tipo: 'data', largura: 'meia' },
        ] as DefCampo[])
      : []),
    { nome: 'email', rotulo: t('campos.email'), tipo: 'email', largura: 'meia', ajuda: edicao ? undefined : t('campos.emailAjuda') },
    ...(pessoais ? ([{ nome: 'telefone', rotulo: t('campos.telefone'), tipo: 'telefone', largura: 'meia' }] as DefCampo[]) : []),
    { nome: 'contrato_tipo', rotulo: t('campos.contrato'), tipo: 'selecao', obrigatorio: true, largura: 'terco', opcoes: CONTRATOS.map((c) => ({ valor: c, rotulo: t(`contrato.${c}`) })) },
    { nome: 'admissao', rotulo: t('campos.admissao'), tipo: 'data', obrigatorio: !!funcionario?.admissao, largura: 'terco' },
    { nome: 'fim_contrato', rotulo: t('campos.fimContrato'), tipo: 'data', largura: 'terco' },
    ...(verSalario ? ([{ nome: 'salario_centavos', rotulo: t('campos.salario'), tipo: 'dinheiro', largura: 'meia' }] as DefCampo[]) : []),
    { nome: 'calendario_id', rotulo: t('campos.calendario'), tipo: 'busca', largura: 'meia', busca: { caminho: '/pessoas/calendarios', rotulo: (i) => String(i.nome) } },
    { nome: 'departamento_id', rotulo: t('campos.departamento'), tipo: 'busca', largura: 'meia', busca: { caminho: '/pessoas/departamentos', rotulo: (i) => String(i.nome) } },
    { nome: 'funcao_id', rotulo: t('campos.funcao'), tipo: 'busca', largura: 'meia', busca: { caminho: '/pessoas/funcoes', rotulo: (i) => String(i.nome) } },
    {
      nome: 'gestor_id',
      rotulo: t('campos.gestor'),
      tipo: 'busca',
      busca: { caminho: '/pessoas/funcionarios', params: { estado: 'ativo' }, rotulo: (i) => String(i.nome) },
    },
    ...(edicao
      ? ([
          { nome: 'vigencia', rotulo: t('campos.vigencia'), tipo: 'data', largura: 'terco', ajuda: t('campos.vigenciaAjuda') },
          { nome: 'motivo', rotulo: t('campos.motivo'), tipo: 'texto', largura: 'meia' },
        ] as DefCampo[])
      : ([
          { nome: 'convidar', rotulo: t('campos.convidar'), tipo: 'booleano', ajuda: t('campos.convidarAjuda'), visivel: (v) => !!v.email },
          {
            nome: 'papel_id',
            rotulo: t('campos.papelConvite'),
            tipo: 'selecao',
            obrigatorio: true,
            ajuda: t('campos.papelConviteAjuda'),
            visivel: (v) => !!v.email && v.convidar !== false,
            opcoes: (papeis.data ?? []).map((p) => ({ valor: p.id, rotulo: nomeDoPapel(t, p) })),
          },
          // Val7 (29/10, ficha FUN_formas-de-acesso): as duas formas de acesso (ao menos uma); o convite vai ao e-mail da ficha com o
          // link para criar a senha e, com a caixa do usuário, o nome de usuário (o "E-mail de acesso" separado saiu).
          { nome: 'login_email', rotulo: t('campos.loginEmail'), tipo: 'booleano', largura: 'meia', visivel: (v) => !!v.email && v.convidar !== false },
          { nome: 'login_usuario', rotulo: t('campos.loginUsuario'), tipo: 'booleano', largura: 'meia', ajuda: t('campos.loginAjuda'), visivel: (v) => !!v.email && v.convidar !== false },
          { nome: 'usuario', rotulo: t('campos.usuarioLogin'), tipo: 'texto', largura: 'meia', ajuda: t('campos.usuarioLoginAjuda'), visivel: (v) => !!v.email && v.convidar !== false && v.login_usuario !== false },
        ] as DefCampo[])),
  ];

  const inicial: Record<string, unknown> = funcionario
    ? { ...funcionario }
    : { contrato_tipo: 'clt', convidar: true, login_email: true, login_usuario: true, admissao: hojeIso() };

  return (
    <Drawer
      aberto={aberto}
      aoFechar={aoFechar}
      largura="lg"
      titulo={funcionario ? nomePessoa(funcionario.nome) : t('acoes.admitir')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-funcionario">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <Formulario
        key={funcionario?.id ?? 'novo'}
        id="form-funcionario"
        campos={campos}
        telaCampos="pessoas.funcionario"
        entidade={pessoais ? 'pessoas.funcionario' : undefined}
        inicial={inicial}
        aoMudar={edicao ? undefined : setValores}
        ajuste={edicao ? undefined : ajuste}
        rotulosAtuais={funcionario ? { departamento_id: funcionario.departamento_nome ?? '', funcao_id: funcionario.funcao_nome ?? '', gestor_id: funcionario.gestor_nome ?? '', calendario_id: funcionario.calendario_nome ?? '' } : undefined}
        aoEnviar={async (v) => {
          const corpo = limpar({
            nome: v.nome,
            ...(pessoais ? { cpf: v.cpf, nascimento: v.nascimento, telefone: v.telefone } : {}),
            email: v.email,
            contrato_tipo: v.contrato_tipo,
            admissao: v.admissao,
            fim_contrato: v.fim_contrato,
            ...(verSalario ? { salario_centavos: v.salario_centavos ?? null } : {}),
            calendario_id: v.calendario_id,
            departamento_id: v.departamento_id,
            funcao_id: v.funcao_id,
            gestor_id: v.gestor_id,
            ...(pessoais ? { extras: v.extras ?? {} } : {}),
          });
          let id = funcionario?.id ?? '';
          if (funcionario) await patch(`/pessoas/funcionarios/${funcionario.id}`, { ...corpo, vigencia: v.vigencia || null, motivo: v.motivo || null });
          else {
            const convida = !!v.email && v.convidar !== false;
            if (convida && v.login_email === false && v.login_usuario === false) throw new Error(t('campos.formaObrigatoria'));
            const acesso = convida ? { login_email: v.login_email !== false, login_usuario: v.login_usuario !== false, ...limpar({ usuario: v.login_usuario !== false ? v.usuario : undefined }) } : {};
            id = (
              await post<{ id: string }>('/pessoas/funcionarios', {
                ...corpo,
                ...acesso,
                convidar: v.convidar !== false,
                ...(v.email && v.convidar !== false && v.papel_id ? { papel_id: v.papel_id } : {}),
                ...(empresaEscolhida(v) ? { empresa_id: empresaEscolhida(v) } : {}),
              })
            ).id;
          }
          await qc.invalidateQueries({ queryKey: ['/pessoas/funcionarios'] });
          await qc.invalidateQueries({ queryKey: ['pessoas'] });
          await qc.invalidateQueries({ queryKey: ['chatter'] });
          toast.sucesso(tn('comum.salvo'));
          aoFechar();
          aoSalvar?.(id);
        }}
      >
        {!edicao && aberto && (
          <AvisoDuplicidade
            valores={valores}
            aoAcrescentar={(id) => {
              aoFechar();
              aoSalvar?.(id);
            }}
          />
        )}
      </Formulario>
    </Drawer>
  );
}
